import {
  type ConversationSettings,
  type CrowdexSavedAgent,
  conversationSettingsSchema,
} from '@crowdex/agent-contracts';
import { apiResponse } from '@crowdex/host-kit/renderer/http';
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useAccountAgentProfiles } from '@/hooks/use-account-agent-profiles';

/** What the page lets the user choose for a conversation; its model is restored with it. */
export type ChosenConversationSettings = Omit<ConversationSettings, 'modelId'>;

const DEFAULTS: ChosenConversationSettings = {
  agentProfileId: null,
  sessionConfigValues: {},
  permissionMode: 'allow-all',
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The conversations' settings this page knows: what the server saved with each (read once), and
 * what the user chose since, which its next turn sends and the server saves.
 */
const conversations = new Map<
  string,
  { saved: ChosenConversationSettings | null; chosen: Partial<ChosenConversationSettings> }
>();
const listeners = new Set<() => void>();
const changed = () => {
  for (const listener of [...listeners]) listener();
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

function entry(conversationId: string) {
  let found = conversations.get(conversationId);
  if (found === undefined) {
    // A conversation not saved yet (no id of the server's) starts with the defaults.
    found = { saved: UUID.test(conversationId) ? null : DEFAULTS, chosen: {} };
    conversations.set(conversationId, found);
  }
  return found;
}

async function load(conversationId: string): Promise<void> {
  const current = entry(conversationId);
  if (current.saved !== null) return;
  let saved: ChosenConversationSettings = DEFAULTS;
  try {
    const response = await apiResponse(
      `/api/conversations/${encodeURIComponent(conversationId)}/settings`,
      { credentials: 'include' },
    );
    const parsed = conversationSettingsSchema.safeParse(await response.json());
    if (parsed.success) {
      const { modelId: _model, ...chosen } = parsed.data;
      saved = chosen;
    }
  } catch {
    // Unreadable: the defaults, which the next turn replaces with what the user chooses.
  }
  current.saved = saved;
  changed();
}

/** Forgets what the page knows of the conversations' settings (tests). */
export function resetConversationSettings(): void {
  conversations.clear();
  changed();
}

/**
 * A conversation's settings (its saved agent, its agent's options, its permissions): saved with
 * it by the server, chosen here, sent with each turn.
 */
export function useConversationSettings(conversationId: string) {
  useEffect(() => {
    void load(conversationId);
  }, [conversationId]);
  const snapshot = useSyncExternalStore(
    subscribe,
    useCallback(() => {
      const current = entry(conversationId);
      return JSON.stringify([current.saved, current.chosen]);
    }, [conversationId]),
    () => JSON.stringify([null, {}]),
  );
  const settings = useMemo<ChosenConversationSettings & { loaded: boolean }>(() => {
    const [saved, chosen] = JSON.parse(snapshot) as [
      ChosenConversationSettings | null,
      Partial<ChosenConversationSettings>,
    ];
    return { ...DEFAULTS, ...saved, ...chosen, loaded: saved !== null };
  }, [snapshot]);
  const choose = useCallback(
    (next: Partial<ChosenConversationSettings>) => {
      const current = entry(conversationId);
      current.chosen = { ...current.chosen, ...next };
      changed();
    },
    [conversationId],
  );
  return { ...settings, choose };
}

/** The account's saved agent a conversation works as, chosen in its composer. */
export function useAiAgentSelection(conversationId: string) {
  const { agentProfileId, loaded, choose } = useConversationSettings(conversationId);
  const { profiles } = useAccountAgentProfiles();
  const profile = useMemo<CrowdexSavedAgent | null>(
    () =>
      agentProfileId === null
        ? null
        : (profiles.find((candidate) => candidate.id === agentProfileId) ?? null),
    [agentProfileId, profiles],
  );
  const select = useCallback(
    (next: CrowdexSavedAgent | null) => choose({ agentProfileId: next?.id ?? null }),
    [choose],
  );
  return { hydrated: loaded, profile, profileId: agentProfileId, select };
}
